import { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { VcfGuardrail } from '@/hooks/useWorkstreams';
import { useGuardrailDefinitions, type GuardrailDefinition } from '@/hooks/useGuardrailDefinitions';
import { cn } from '@/lib/utils';

/**
 * Picker-based guardrail modal.
 *
 * - When creating: shows a definition picker first, then a factor configuration form.
 * - When editing: shows only the factor configuration form (definition is read-only).
 */
export function MasterGuardrailModal({
  guardrail,
  capabilityActivities = [],
  linkedActivityIds = [],
  existingDefinitionIds = [],
  onSave,
  onClose,
}: {
  guardrail: VcfGuardrail | null;
  capabilityActivities?: Array<{ id: string; name: string }>;
  linkedActivityIds?: string[];
  /** Definition IDs already assigned to this capability (to exclude from picker) */
  existingDefinitionIds?: string[];
  onSave: (data: {
    name: string;
    code: string;
    baseline?: string;
    multiplier?: string;
    baselineValue?: number;
    multiplierFactor?: number;
    multiplierStep?: number;
    multiplierLabel?: string;
    optionFactors?: Record<string, number>;
    inputType?: 'number' | 'select' | 'boolean';
    options?: string[];
    defaultValue?: string;
    minValue?: number;
    maxValue?: number;
    linkedActivityIds?: string[];
    definitionId?: string;
  }) => void;
  onClose: () => void;
}) {
  const { data: definitionsData } = useGuardrailDefinitions();
  const definitions = definitionsData?.definitions || [];

  // Phase: 'pick' (choose definition) or 'configure' (set factors)
  const [phase, setPhase] = useState<'pick' | 'configure'>(guardrail ? 'configure' : 'pick');
  const [selectedDef, setSelectedDef] = useState<GuardrailDefinition | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  // Factor form state
  const [baselineValue, setBaselineValue] = useState(guardrail?.baselineValue?.toString() || '0');
  const [multiplierFactor, setMultiplierFactor] = useState(guardrail?.multiplierFactor?.toString() || '0');
  const [multiplierStep, setMultiplierStep] = useState(guardrail?.multiplierStep?.toString() || '1');
  const [multiplierLabel, setMultiplierLabel] = useState(guardrail?.multiplierLabel || '');
  const [defaultValue, setDefaultValue] = useState(guardrail?.defaultValue || '');
  const [optionFactors, setOptionFactors] = useState<Record<string, string>>(
    guardrail?.optionFactors
      ? Object.fromEntries(Object.entries(guardrail.optionFactors).map(([k, v]) => [k, v.toString()]))
      : {}
  );
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>(linkedActivityIds);

  const searchRef = useRef<HTMLInputElement>(null);

  // Auto-focus search in picker mode
  useEffect(() => {
    if (phase === 'pick' && searchRef.current) {
      searchRef.current.focus();
    }
  }, [phase]);

  // The definition to display (either the picked one or inferred from the editing guardrail)
  const activeDef = selectedDef || (guardrail ? {
    inputType: guardrail.inputType,
    options: guardrail.options,
    name: guardrail.name,
    code: guardrail.code,
    description: null as string | null,
    minValue: guardrail.minValue,
    maxValue: guardrail.maxValue,
  } : null);

  // Filter definitions: exclude already-assigned ones
  const assignedIds = new Set(existingDefinitionIds);
  const availableDefs = definitions.filter((d) => d.isActive && !assignedIds.has(d.id));
  const filteredDefs = pickerSearch
    ? availableDefs.filter(
        (d) =>
          d.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
          d.code.toLowerCase().includes(pickerSearch.toLowerCase())
      )
    : availableDefs;

  const handlePickDef = (def: GuardrailDefinition) => {
    setSelectedDef(def);
    setPhase('configure');
    // Reset factors for the new definition
    setBaselineValue('0');
    setMultiplierFactor('0');
    setMultiplierStep('1');
    setMultiplierLabel('');
    setDefaultValue('');
    setOptionFactors({});
  };

  const toggleActivity = (activityId: string) => {
    setSelectedActivityIds((prev) =>
      prev.includes(activityId)
        ? prev.filter((id) => id !== activityId)
        : [...prev, activityId]
    );
  };

  const handleSave = () => {
    const mf = parseFloat(multiplierFactor) || 0;
    const bv = parseFloat(baselineValue) || 0;
    const ms = parseFloat(multiplierStep) || 1;

    const inputType = activeDef?.inputType || 'number';
    const autoLabel = multiplierLabel || (inputType === 'number' ? `per ${ms > 1 ? ms + ' ' : ''}additional` : '');
    const autoMultiplier = mf > 0 ? `+${mf}x ${autoLabel}` : undefined;
    const autoBaseline = inputType === 'number' ? bv.toString() : undefined;

    let options: string[] | undefined;
    let parsedOptionFactors: Record<string, number> | undefined;

    if (inputType === 'select' && activeDef?.options) {
      options = activeDef.options;
      parsedOptionFactors = {};
      for (const opt of activeDef.options) {
        parsedOptionFactors[opt] = parseFloat(optionFactors[opt] || '0') || 0;
      }
    }

    onSave({
      name: activeDef?.name || '',
      code: activeDef?.code || '',
      inputType,
      baselineValue: bv,
      multiplierFactor: mf,
      multiplierStep: ms,
      multiplierLabel: autoLabel || undefined,
      baseline: autoBaseline,
      multiplier: autoMultiplier,
      options,
      optionFactors: parsedOptionFactors,
      defaultValue: defaultValue || undefined,
      minValue: activeDef?.minValue ?? undefined,
      maxValue: activeDef?.maxValue ?? undefined,
      linkedActivityIds: selectedActivityIds,
      definitionId: selectedDef?.id,
    });
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header flex items-center justify-between">
          <h2 className="dialog-title">
            {guardrail
              ? 'Edit Guardrail Factors'
              : phase === 'pick'
                ? 'Pick a Guardrail Definition'
                : 'Configure Guardrail'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Phase 1: Picker */}
        {phase === 'pick' && (
          <div className="dialog-body">
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search definitions..."
                className="input w-full pl-8 text-sm"
              />
            </div>
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {filteredDefs.length === 0 ? (
                <p className="px-3 py-6 text-sm text-gray-500 text-center">
                  {availableDefs.length === 0
                    ? 'All definitions are already assigned to this capability.'
                    : 'No matching definitions.'}
                </p>
              ) : (
                filteredDefs.map((def) => (
                  <button
                    key={def.id}
                    onClick={() => handlePickDef(def)}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-gray-500">{def.code}</span>
                      <span className={cn(
                        'text-[10px] uppercase px-1.5 py-0.5 rounded font-medium',
                        def.inputType === 'number' && 'bg-blue-100 text-blue-700',
                        def.inputType === 'select' && 'bg-purple-100 text-purple-700',
                        def.inputType === 'boolean' && 'bg-amber-100 text-amber-700',
                      )}>
                        {def.inputType}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">{def.name}</div>
                    {def.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{def.description}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Phase 2: Configure factors */}
        {phase === 'configure' && activeDef && (
          <div className="dialog-body space-y-4">
            {/* Definition info (read-only) */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-500">{activeDef.code}</span>
                <span className={cn(
                  'text-[10px] uppercase px-1.5 py-0.5 rounded font-medium',
                  activeDef.inputType === 'number' && 'bg-blue-100 text-blue-700',
                  activeDef.inputType === 'select' && 'bg-purple-100 text-purple-700',
                  activeDef.inputType === 'boolean' && 'bg-amber-100 text-amber-700',
                )}>
                  {activeDef.inputType}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-900 mt-1">{activeDef.name}</div>
              {activeDef.description && (
                <div className="text-xs text-gray-500 mt-1">{activeDef.description}</div>
              )}
            </div>

            {/* Factor fields by input type */}
            {activeDef.inputType === 'number' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Baseline Value</label>
                    <input type="number" value={baselineValue} onChange={(e) => setBaselineValue(e.target.value)} className="input mt-1 w-full" placeholder="0" step="any" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Included threshold</p>
                  </div>
                  <div>
                    <label className="label">Factor per Step</label>
                    <input type="number" value={multiplierFactor} onChange={(e) => setMultiplierFactor(e.target.value)} className="input mt-1 w-full" placeholder="0.5" step="0.05" min="0" />
                    <p className="text-[10px] text-gray-400 mt-0.5">e.g., 0.5 = +0.5x</p>
                  </div>
                  <div>
                    <label className="label">Step Size</label>
                    <input type="number" value={multiplierStep} onChange={(e) => setMultiplierStep(e.target.value)} className="input mt-1 w-full" placeholder="1" step="any" min="1" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Units per step</p>
                  </div>
                </div>
                <div>
                  <label className="label">Display Label</label>
                  <input type="text" value={multiplierLabel} onChange={(e) => setMultiplierLabel(e.target.value)} className="input mt-1 w-full" placeholder="e.g., per additional DC" />
                </div>
                <div>
                  <label className="label">Default Value</label>
                  <input type="text" value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} className="input mt-1 w-full" />
                </div>
              </>
            )}

            {activeDef.inputType === 'boolean' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Factor When Enabled</label>
                  <input type="number" value={multiplierFactor} onChange={(e) => setMultiplierFactor(e.target.value)} className="input mt-1 w-full" placeholder="0.5" step="0.05" min="0" />
                  <p className="text-[10px] text-gray-400 mt-0.5">Added when toggled on</p>
                </div>
                <div>
                  <label className="label">Display Label</label>
                  <input type="text" value={multiplierLabel} onChange={(e) => setMultiplierLabel(e.target.value)} className="input mt-1 w-full" placeholder="e.g., if required" />
                </div>
              </div>
            )}

            {activeDef.inputType === 'select' && activeDef.options && (
              <>
                <div>
                  <label className="label">Options (from definition)</label>
                  <div className="text-sm text-gray-700 mt-1 bg-gray-50 rounded px-3 py-2 font-mono">
                    {activeDef.options.join(', ')}
                  </div>
                </div>
                <div>
                  <label className="label">Factor per Option</label>
                  <div className="space-y-2 mt-1">
                    {activeDef.options.map((opt) => (
                      <div key={opt} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-32 truncate">{opt}</span>
                        <input
                          type="number"
                          value={optionFactors[opt] || '0'}
                          onChange={(e) => setOptionFactors((prev) => ({ ...prev, [opt]: e.target.value }))}
                          className="input w-24 text-center"
                          step="0.05"
                          min="0"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-400">x</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">Display Label</label>
                  <input type="text" value={multiplierLabel} onChange={(e) => setMultiplierLabel(e.target.value)} className="input mt-1 w-full" placeholder="e.g., for complex/regulated" />
                </div>
              </>
            )}

            {/* Applies To Activities section */}
            {capabilityActivities.length > 0 && (
              <div>
                <label className="label">Applies To Activities</label>
                <p className="text-[10px] text-gray-400 mt-0.5 mb-2">
                  {selectedActivityIds.length === 0
                    ? 'No activities selected -- guardrail applies to all activities in this capability.'
                    : `${selectedActivityIds.length} of ${capabilityActivities.length} activities selected.`}
                </p>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {capabilityActivities.map((activity) => (
                    <label
                      key={activity.id}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedActivityIds.includes(activity.id)}
                        onChange={() => toggleActivity(activity.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 truncate">{activity.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="dialog-footer">
          {phase === 'configure' && !guardrail && (
            <button type="button" onClick={() => { setPhase('pick'); setSelectedDef(null); }} className="btn-secondary mr-auto">
              Back
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          {phase === 'configure' && (
            <button onClick={handleSave} className="btn-primary" disabled={!activeDef}>
              {guardrail ? 'Save' : 'Add'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
